"use client"

import { Button } from "@repo/ui/components/ui/button"
import { ThemeToggle } from "@repo/ui/components/ui/theme-toggle"
import { useLabels } from "@repo/ui/hooks/use-labels"
import { cn } from "@repo/ui/lib/utils"
import { motion, useScroll, useSpring } from "framer-motion"
import { Menu, X } from "lucide-react"
import { useRouter } from "@repo/ui/hooks/use-router"
import { useState, useEffect } from "react"
import { useHomeSettings } from "../../lib/home-settings"

export function Header(): JSX.Element {
  const router = useRouter()
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false)
  const [isScrolled, setIsScrolled] = useState(false)
  const [activeSection, setActiveSection] = useState<string>("")
  const { labels } = useLabels()
  const homeSettings = useHomeSettings()
  const headerLabels = labels.home.header

  // Scroll progress bar
  const { scrollYProgress } = useScroll()
  const scaleX = useSpring(scrollYProgress, {
    stiffness: 100,
    damping: 30,
    restDelta: 0.001,
  })

  // Detect scroll past hero
  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 80)
    window.addEventListener("scroll", handleScroll, { passive: true })
    handleScroll()
    return () => window.removeEventListener("scroll", handleScroll)
  }, [])

  // Track active section via IntersectionObserver
  useEffect(() => {
    const sectionIds = ["services", "pricing", "about", "contact"]
    const observers = sectionIds.map((id) => {
      const el = document.getElementById(id)
      if (!el) return null
      const observer = new IntersectionObserver(
        ([entry]) => {
          if (entry.isIntersecting) setActiveSection(id)
        },
        { threshold: 0.2, rootMargin: "-80px 0px -50% 0px" },
      )
      observer.observe(el)
      return observer
    })
    return () => {
      observers.forEach((o) => o?.disconnect())
    }
  }, [])

  const navItems = [
    { name: headerLabels.menu.services, href: "#services", id: "services" },
    { name: headerLabels.menu.pricing, href: "#pricing", id: "pricing" },
    { name: headerLabels.menu.about, href: "#about", id: "about" },
    { name: headerLabels.menu.contact, href: "#contact", id: "contact" },
  ]

  // At hero top: transparent dark header blends with hero background
  const atTop = !isScrolled

  return (
    <>
      {/* ─── Scroll progress bar ────────────────────────────────────── */}
      <motion.div
        className="fixed top-0 left-0 right-0 h-[2px] bg-primary origin-left z-[60] pointer-events-none"
        style={{ scaleX }}
        aria-hidden
      />

      <motion.header
        initial={{ y: -100, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.6, ease: "easeOut" }}
        className={cn(
          "sticky top-0 z-50 transition-all duration-500",
          atTop
            ? "bg-[#0d2e1a] border-b border-white/10"
            : "border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60 shadow-sm border-border",
        )}
      >
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-4">
            {/* ─── Logo ───────────────────────────────────────────── */}
            <motion.button
              type="button"
              className="flex items-center focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary rounded-sm"
              whileHover={{ scale: 1.03 }}
              transition={{ type: "spring", stiffness: 400, damping: 10 }}
              onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
              aria-label="Retour en haut de page"
            >
              <img
                src={atTop ? "/images/logo-name/white.svg" : "/images/logo-name/dark.svg"}
                alt={homeSettings.brand.logo.text}
                className="h-10 w-auto transition-opacity duration-300"
              />
            </motion.button>

            {/* ─── Desktop nav ────────────────────────────────────── */}
            <nav
              className="hidden md:flex items-center space-x-8"
              role="navigation"
              aria-label="Navigation principale"
            >
              {navItems.map((item, index) => (
                <motion.div
                  key={item.name}
                  initial={{ opacity: 0, y: -20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1 + 0.3, duration: 0.5 }}
                >
                  <a
                    href={item.href}
                    className={cn(
                      "relative text-sm font-medium transition-colors duration-300 group",
                      atTop
                        ? "text-white/70 hover:text-white"
                        : activeSection === item.id
                          ? "text-primary"
                          : "text-muted-foreground hover:text-primary",
                    )}
                  >
                    {item.name}
                    <span
                      className={cn(
                        "absolute -bottom-0.5 left-0 h-[1.5px] bg-current rounded-full transition-all duration-300",
                        activeSection === item.id ? "w-full" : "w-0 group-hover:w-full",
                      )}
                    />
                  </a>
                </motion.div>
              ))}
            </nav>

            {/* ─── Actions ────────────────────────────────────────── */}
            <motion.div
              className="flex items-center space-x-3"
              initial={{ opacity: 0, x: 50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.4, duration: 0.5 }}
            >
              <ThemeToggle />
              <Button
                variant="ghost"
                className={cn(
                  "hidden md:inline-flex text-sm",
                  atTop && "text-white/80 hover:text-white hover:bg-white/10",
                )}
                onClick={() => router.push("/auth")}
              >
                {headerLabels.actions.login}
              </Button>
              <Button
                variant="ghost"
                size="icon"
                className={cn("md:hidden", atTop && "text-white hover:bg-white/10")}
                onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                aria-expanded={isMobileMenuOpen}
                aria-label={isMobileMenuOpen ? "Fermer le menu" : "Ouvrir le menu"}
              >
                {isMobileMenuOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
              </Button>
            </motion.div>
          </div>
        </div>

        {/* ─── Mobile Menu ──────────────────────────────────────────── */}
        {isMobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.3 }}
            className={cn(
              "md:hidden border-t",
              atTop ? "bg-[#0d2e1a] border-white/10" : "bg-background border-border",
            )}
          >
            <div className="container mx-auto px-4 py-4">
              <nav
                className="flex flex-col space-y-1"
                role="navigation"
                aria-label="Navigation mobile"
              >
                {navItems.map((item, index) => (
                  <motion.div
                    key={item.name}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: index * 0.08, duration: 0.3 }}
                  >
                    <a
                      href={item.href}
                      className={cn(
                        "block py-2.5 px-3 rounded-md text-sm font-medium transition-colors",
                        atTop
                          ? "text-white/75 hover:text-white hover:bg-white/10"
                          : "text-muted-foreground hover:text-primary hover:bg-accent",
                      )}
                      onClick={() => setIsMobileMenuOpen(false)}
                    >
                      {item.name}
                    </a>
                  </motion.div>
                ))}
                <motion.div
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.4, duration: 0.3 }}
                  className={cn("pt-4 border-t space-y-2", atTop ? "border-white/10" : "border-border")}
                >
                  <Button
                    variant="ghost"
                    className={cn(
                      "w-full justify-start",
                      atTop && "text-white/80 hover:text-white hover:bg-white/10",
                    )}
                    onClick={() => {
                      router.push("/auth")
                      setIsMobileMenuOpen(false)
                    }}
                  >
                    {headerLabels.actions.login}
                  </Button>
                </motion.div>
              </nav>
            </div>
          </motion.div>
        )}
      </motion.header>
    </>
  )
}
